import { useCallback, useEffect, useState } from "react";
import { fetchWallets, type ApiErrorInfo } from "@/lib/api";
import type { Wallet } from "@/types";
import { MOCK_WALLETS } from "@/data/mock";

interface UseWalletsResult {
  wallets: Wallet[];
  loading: boolean;
  error: ApiErrorInfo | null;
  source: "api" | "mock";
  refresh: () => void;
}

function mapApiWallet(raw: Record<string, unknown>): Wallet {
  const id = String(raw.walletId ?? raw.id ?? "");
  const alias = String(raw.accountAlias ?? raw.name ?? raw.label ?? id);
  const chain = String(raw.chain ?? raw.network ?? "Ethereum Sepolia");
  const provider = String(raw.provider ?? raw.custodian ?? "Mesh (VaultCore)");
  const rawType = String(raw.type ?? raw.walletType ?? "hot").toLowerCase();
  const type = (rawType === "cold" || rawType === "warm" ? rawType : "hot") as Wallet["type"];
  const rawStatus = String(raw.status ?? "active").toLowerCase();
  const status: Wallet["status"] =
    rawStatus === "frozen" ? "frozen"
    : rawStatus === "pending" ? "pending"
    : rawStatus.includes("sandbox") || rawStatus.includes("test") ? "sandbox"
    : "active";
  const balanceUsd = typeof raw.balanceUsd === "number" ? raw.balanceUsd : null;
  const lastSyncAgo = typeof raw.lastSyncAgo === "string" ? raw.lastSyncAgo : null;

  return { id, name: alias, chain, provider, type, balanceUsd, lastSyncAgo, status };
}

export function useWallets(): UseWalletsResult {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const [source, setSource] = useState<"api" | "mock">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWallets();
      if (result.error) {
        setWallets([]);
        setSource("api");
        setError(result.error);
      } else if (result.wallets.length === 0) {
        setWallets(MOCK_WALLETS);
        setSource("mock");
      } else {
        setWallets(result.wallets.map(mapApiWallet));
        setSource("api");
      }
    } catch {
      setWallets([]);
      setSource("api");
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { wallets, loading, error, source, refresh: load };
}
